import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Modal, Button, Card, Spinner } from "react-bootstrap";
import { AlertTriangle } from "lucide-react";
import {
  FaUser,
  FaCoins,
  FaShieldAlt,
  FaCog,
  FaQuestionCircle,
  FaTrashAlt,
  FaSignOutAlt,
  FaChevronRight,
} from "react-icons/fa";
import axios from "axios";
import women from "../../../assets/images/women.png";
import "../../../assets/css/Customer/CustomerSettings/CustomerSetting.css";
import CustomerMasterLayout from "../../../layouts/Customer/CustomerMasterLayout";
import AppContext from "../../../context/AppContext";

const CustomerSetting = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(AppContext);

  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    totalCoins: 0,
  });
  const [loading, setLoading] = useState(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleClose = () => setShowDeleteModal(false);
  const handleShow = () => setShowDeleteModal(true);
  const handleLogoutClose = () => setShowLogoutModal(false);
  const handleLogoutShow = () => setShowLogoutModal(true);

  useEffect(() => {
    const storedCustomer = localStorage.getItem("tdcustomer");
    if (storedCustomer) {
      try {
        const parsed = JSON.parse(storedCustomer);
        setUserInfo({
          name: parsed.name || parsed.fullname || "",
          email: parsed.email || "",
          totalCoins: parsed.totalCoins || parsed.coins || 0,
        });
      } catch (e) {
        console.log("Could not parse stored customer:", e);
      }
    }

    const token = localStorage.getItem("authToken");
    axios
      .post(`${baseUrl}/customer/profile/viewprofile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success) {
          const c = response.data.tdcustomer;
          if (c) {
            setUserInfo({
              name: c.customerName || "",
              email: c.customerEmail || "",
              totalCoins: c.totalCoins ?? c.coins ?? 0,
            });
            localStorage.setItem("tdcustomer", JSON.stringify(c));
          }
        }
      })
      .catch((error) => {
        console.log("Profile fetch error:", error);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const generalOptions = [
    {
      icon: <FaUser />,
      label: "Profile settings",
      link: "/customer-profile-setting",
    },
    { icon: <FaCoins />, label: "Coins Wallet", link: "/customer-coin-wallet" },
    { icon: <FaShieldAlt />, label: "Security", link: "/customer-security" },
  ];

  const appDetailsOptions = [
    { icon: <FaCog />, label: "App Setting", link: "/customer-app-setting" },
    {
      icon: <FaQuestionCircle />,
      label: "Help Center",
      link: "/customer-help-center",
    },
  ];

  const alertZoneOptions = [
    { icon: <FaTrashAlt />, label: "Delete account", danger: true },
    { icon: <FaSignOutAlt />, label: "Logout", danger: true },
  ];

  return (
    <CustomerMasterLayout screenName="Setting">
      <div className="CustomerSetting-wrapper">
        <Container className="CustomerSetting-container">
          {/* Profile Section */}
          <div className="CustomerSetting-profile-section">
            <img src={women} alt="Profile" className="CustomerSetting-avatar" />
            <div className="CustomerSetting-user-info">
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <h2 className="CustomerSetting-user-name">
                    {userInfo.name || "User"}
                  </h2>
                  <p className="CustomerSetting-user-email">
                    {userInfo.email || ""}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Coins Wallet Card */}
          <div className="CustomerSetting-wallet-card">
            <span className="CustomerSetting-wallet-label">🪙 Total Coins</span>
            <span className="CustomerSetting-wallet-amount">
              {loading ? "..." : `${userInfo.totalCoins} Coins`}
            </span>
          </div>

          {/* General Section */}
          <div className="CustomerSetting-section">
            <h3 className="CustomerSetting-section-title">General</h3>
            <Card className="CustomerSetting-section-card">
              <Card.Body className="CustomerSetting-card-body">
                {generalOptions.map((option, index) => (
                  <div
                    key={index}
                    className="CustomerSetting-item"
                    onClick={() => navigate(option.link)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="CustomerSetting-item-left">
                      <span className="CustomerSetting-icon">
                        {option.icon}
                      </span>
                      <span className="CustomerSetting-label">
                        {option.label}
                      </span>
                    </div>
                    <FaChevronRight className="CustomerSetting-chevron" />
                  </div>
                ))}
              </Card.Body>
            </Card>
          </div>

          {/* App Details Section */}
          <div className="CustomerSetting-section">
            <h3 className="CustomerSetting-section-title">App Details</h3>
            <Card className="CustomerSetting-section-card">
              <Card.Body className="CustomerSetting-card-body">
                {appDetailsOptions.map((option, index) => (
                  <div
                    key={index}
                    className="CustomerSetting-item"
                    onClick={() => navigate(option.link)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="CustomerSetting-item-left">
                      <span className="CustomerSetting-icon">
                        {option.icon}
                      </span>
                      <span className="CustomerSetting-label">
                        {option.label}
                      </span>
                    </div>
                    <FaChevronRight className="CustomerSetting-chevron" />
                  </div>
                ))}
              </Card.Body>
            </Card>
          </div>

          {/* Alert Zone Section */}
          <div className="CustomerSetting-section">
            <h3 className="CustomerSetting-section-title CustomerSetting-alert-title">
              Alert Zone
            </h3>
            <Card className="CustomerSetting-section-card CustomerSetting-alert-zone-card">
              <Card.Body className="CustomerSetting-card-body">
                {alertZoneOptions.map((option, index) => (
                  <div
                    key={index}
                    className="CustomerSetting-item clickable-item"
                    onClick={() =>
                      option.label === "Delete account"
                        ? handleShow()
                        : handleLogoutShow()
                    }
                  >
                    <div className="CustomerSetting-item-left">
                      <span className="CustomerSetting-icon CustomerSetting-danger-icon">
                        {option.icon}
                      </span>
                      <span className="CustomerSetting-label CustomerSetting-danger-label">
                        {option.label}
                      </span>
                    </div>
                    <FaChevronRight className="CustomerSetting-chevron" />
                  </div>
                ))}
              </Card.Body>
            </Card>
          </div>

          {/* Delete Modal */}
          <Modal
            show={showDeleteModal}
            onHide={handleClose}
            centered
            contentClassName="CustomerSetting-delete-modal-content"
          >
            <Modal.Body className="text-center p-4">
              <div className="CustomerSetting-delete-icon-container">
                <AlertTriangle size={50} color="#1B9B82" />
              </div>
              <h3 className="fw-bold mb-2">Delete Your Account</h3>
              <p className="text-muted mb-4">
                You will lose your data by deleting your account.
              </p>
              <div className="d-flex gap-3">
                <Button
                  className="w-100 CustomerSetting-modal-btn-cancel"
                  onClick={handleClose}
                >
                  No, Keep it
                </Button>
                <Button className="w-100 CustomerSetting-modal-btn-delete">
                  Yes, Delete
                </Button>
              </div>
            </Modal.Body>
          </Modal>

          {/* Logout Modal */}
          <Modal
            show={showLogoutModal}
            onHide={handleLogoutClose}
            centered
            contentClassName="CustomerSetting-delete-modal-content"
          >
            <Modal.Body className="text-center p-4">
              <div
                className="CustomerSetting-delete-icon-container"
                style={{ fontSize: "50px" }}
              >
                🚪
              </div>
              <h3 className="fw-bold mb-2">Come Back Soon</h3>
              <p className="text-muted mb-4">
                Are you sure you want to logout?
              </p>
              <div className="d-flex gap-3">
                <Button
                  className="w-100 CustomerSetting-modal-btn-cancel"
                  onClick={handleLogoutClose}
                >
                  Stay Logged In
                </Button>
                <Button
                  className="w-100 CustomerSetting-modal-btn-delete"
                  onClick={() => {
                    localStorage.removeItem("authToken");
                    localStorage.removeItem("actorType");
                    localStorage.removeItem("customer");
                    setShowLogoutModal(false);
                    navigate("/customer-login");
                  }}
                >
                  Yes, Logout
                </Button>
              </div>
            </Modal.Body>
          </Modal>
        </Container>
      </div>
    </CustomerMasterLayout>
  );
};

export default CustomerSetting;